import React, {
  useEffect,
  useMemo,
  useState,
} from 'react'
import { useParams } from 'react-router-dom'
import { GroupNavi, HeaderNavi } from '../../components/navigation'
import Loading from '../../components/common/Loading'
import { useAppDispatch, useAppSelector } from '../../hooks'
import { getTeamGroups } from '../../modules/Team'
import { getGroup } from '../../modules/Group'
import { selectLoadingState } from '../../store'
import { toId } from '../../utils/commonUtils'
import { showModal } from '../../modules/UI'
import { BASE_MODAL } from '../../components/common/RootModal'
import PrintLabelModal from '../../components/overview/qrPrinting/PrintLabelModal'
import PrintBrochureModal from '../../components/overview/qrPrinting/PrintBrochureModal'
import { getGroupQrTags } from '../../modules/QrTag'
import './GroupQrPrint.css'

type Params = {
  group_id: string
}
const GroupQrPrintPage = () => {
  const { group_id: groupIdParam } = useParams<Params>()
  const dispatch = useAppDispatch()
  const loading = useAppSelector(selectLoadingState)
  const team = useAppSelector((state) => state.team)
  const [group, setGroup] = useState<IGroup | null>(null)
  const [fetchError, setFetchError] = useState<string | null>(null)
  const [isFetchingGroup, setIsFetchingGroup] = useState(false)
  const [qrTagPrintables, setQrTagPrintables] = useState<IPrintableQr[]>([])

  useEffect(() => {
    if (!group) return

    const loadQrTags = async () => {
      setQrTagPrintables([])
      try {
        const tags = await getGroupQrTags(group.id)
        const printables = tags.map((tag) => ({
          id: tag.id,
          name: tag.name,
          image_url: tag.qr_code_info?.image_url ?? null,
          digit_code: tag.qr_code_info?.digit_code ?? null,
          description: tag.description ?? undefined,
        }))
        setQrTagPrintables(printables)
      } catch (error) {
        console.error('Failed to load QR tag printables', error)
        setQrTagPrintables([])
      }
    }

    loadQrTags()
  }, [group])

  useEffect(() => {
    if (!team.groups) {
      dispatch(getTeamGroups(false))
    }
  }, [dispatch, team.groups])

  const targetGroup = useMemo(() => {
    if (!groupIdParam || !team.groups) return null
    const targetId = toId<GroupId>(groupIdParam)
    return team.groups.find((candidate) => toId(candidate.id) === targetId) ?? null
  }, [groupIdParam, team.groups])

  useEffect(() => {
    if (!groupIdParam) return

    if (targetGroup) {
      setGroup(targetGroup)
      return
    }

    const groupId = toId<GroupId>(groupIdParam)
    setIsFetchingGroup(true)
    setFetchError(null)
    dispatch(getGroup({ groupId }))
      .unwrap()
      .then((result) => {
        setGroup(result)
      })
      .catch(() => {
        setFetchError('グループ情報を取得できませんでした。')
      })
      .finally(() => {
        setIsFetchingGroup(false)
      })
  }, [dispatch, groupIdParam, targetGroup])

  const openModal = (format: 'label' | 'booklet') => {
    if (!group) return

    const modalTitle = format === 'label'
      ? 'QRタグを印刷する（シール）'
      : 'QRタグを印刷する（冊子）'

    const content = format === 'label'
      ? <PrintLabelModal group={group} printables={qrTagPrintables} />
      : <PrintBrochureModal group={group} printables={qrTagPrintables} />

    dispatch(showModal({
      modalType: BASE_MODAL,
      modalProps: {
        title: modalTitle,
        onActionConfirmed: undefined,
        children: content,
      },
    }))
  }

  return (
    <div className="content" data-testid="group-qr-print-page">
      <HeaderNavi />
      <Loading show={loading || isFetchingGroup} />
      <GroupNavi>
        <div className="group-qr-print-page">
          <div className="group-qr-print-page__header">
            <h2>QRタグを印刷する</h2>
            {group && (
              <p>
                {group.name}
                {' '}
                に紐づくQRタグを印刷し、対象物に貼り付けて運用してください。
              </p>
            )}
          </div>
          {fetchError && (
            <div className="group-qr-print-page__error">{fetchError}</div>
          )}
          {!group && !fetchError && !isFetchingGroup && (
            <div className="group-qr-print-page__empty">指定されたグループが見つかりません。</div>
          )}
          {group && (
            <div className="group-qr-print-page__content">
              <p className="group-qr-print-page__lead">
                QRタグは対象物に貼り付けるだけで機能します。目的に合わせて印刷形式を選び、最新のQRコードを貼り付けましょう。
              </p>
              <p className="group-qr-print-page__lead">
                シール型は対象物に直接貼り付ける場合、冊子型は複数枚をまとめて管理したい場合に便利です。
              </p>
              <div className="group-qr-print-page__actions">
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => openModal('label')}
                >
                  MAMORIO QRを印刷する（シール）
                </button>
                <button
                  type="button"
                  className="btn btn-primary btn-sm"
                  onClick={() => openModal('booklet')}
                >
                  MAMORIO QRを印刷する（冊子）
                </button>
              </div>
            </div>
          )}
        </div>
      </GroupNavi>
    </div>
  )
}

export default GroupQrPrintPage
